'use client';

import { CurrencyHelpers } from "@/helpers/currencyHelpers";
import { isPrototypeLifecycle } from '@/utils/product-lifecycle';
import React, { useMemo } from "react";
import { useParams } from "next/navigation";
import { checkIsBuilderProduct } from '@/helpers/builder-product-helper';

interface Item {
    name: string;
    quantity: number;
    isProductAvailable: boolean;
    variant: {
        price: { centAmount: number; };
        attributes: Record<string, any>;
    };
}

interface RoomGroup {
    roomName: string;
    items: Item[];
}

interface PortraitSummaryTableProps {
    tableData: { headers: string[]; };
    roomGroups: RoomGroup[];
    showPricesOnPdf: boolean;
    selectDataForPublishedProducts?: any;
}

const PortraitSummaryTable: React.FC<PortraitSummaryTableProps> = ({
    tableData,
    roomGroups,
    showPricesOnPdf,
    selectDataForPublishedProducts,
}) => {
    const params = useParams();
    const locale = (params?.locale as string) || 'au';
    const { rows, totalQuantity, totalAmount } = useMemo(() => {
        let totalQuantity = 0;
        let totalAmount = 0;

        const rows: React.ReactNode[] = [];

        roomGroups?.forEach((room, rowIndex) => {
            let itemCount = 0;

            room.items?.forEach((item, itemIndex) => {
                if (!item.isProductAvailable) return;

                const itemIsPrototype = isPrototypeLifecycle(
                  item.variant?.attributes?.['item-attributes']?.find((attr: any) => attr.name === 'lifecycle')?.value
                );
                const isBuilderProduct = checkIsBuilderProduct(item, selectDataForPublishedProducts, locale);

                totalQuantity += item.quantity;
                if (!itemIsPrototype && !isBuilderProduct) {
                  totalAmount += item.quantity * (item?.variant?.price?.centAmount || 0);
                }

                rows.push(
                    <tr className="border border-black" key={`${rowIndex}-${itemIndex}`}>
                        <td className="ps-td border border-black text-[18px] text-[#4B5563] print:text-xs">
                            {itemCount === 0 ? room.roomName : ''}
                        </td>
                        <td className="ps-td border border-black text-[14px] font-semibold text-[#4B5563]">
                            {item.variant?.attributes?.['item-code']}
                        </td>
                        <td className="ps-td border border-black text-[18px] text-[#4B5563] print:text-xs" >{item.variant.attributes["item-name"]}</td>
                        <td className="ps-td border border-black text-[18px] text-[#4B5563] print:text-xs">{item.quantity}</td>
                        {showPricesOnPdf && (
                            <td className="ps-td border border-black text-[18px] text-[#4B5563] print:text-xs">
                                {!itemIsPrototype && !isBuilderProduct
                                    ? CurrencyHelpers.formatForCurrency(
                                        item.quantity * (item?.variant?.price?.centAmount || 0),
                                        'en',
                                        'USD',
                                        2
                                    ).replace('US', '')
                                    : ''}
                            </td>
                        )}
                    </tr>
                );

                itemCount++;
            });
        });

        return { rows, totalQuantity, totalAmount };
    }, [roomGroups, showPricesOnPdf, selectDataForPublishedProducts, locale]);

    return (
        <>
        <style>{`
            .ps-td {
            padding: 8px;
            }

            @media print {
            .ps-td {
                padding: 0.5rem;
            }
            }
        `}</style>        
        <div className="mt-4 break-after-page overflow-x-auto print:mt-1">
            <table className="w-full border border-black border-collapse rounded-[1px]" style={{ border: '1px' }}>
                <thead>
                    <tr className="bg-[#E7E5EB]">
                        {tableData.headers.map((header, index) => (
                            <th
                                key={index}
                                className="border border-black p-[8px] text-left print:px-2 print:py-2 print:font-semibold print:text-xs"
                            >
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="print:text-xs">
                    {rows}
                    <tr className="border border-black">
                        <td
                            className="border border-black text-[18px] font-semibold text-[#4B5563] px-[8px] py-[8px] text-right font-semibold print:px-2 print:py-2"
                            colSpan={3}
                        >
                            Total
                        </td>
                        <td className="ps-td border border-black border border-black text-[18px] text-[#4B5563] print:text-xs">{totalQuantity}</td>
                        {showPricesOnPdf && (
                            <td className="ps-td border border-black border border-black text-[18px] text-[#4B5563] print:text-xs">
                                {CurrencyHelpers.formatForCurrency(totalAmount, 'en', 'USD', 2).replace('US', '')}
                            </td>
                        )}
                    </tr>
                </tbody>
            </table>
        </div>
        </>
    );
};

export default PortraitSummaryTable;
